import { FunctionComponent, useEffect, useState } from 'react'
import { Router } from 'react-router-dom'
import { createBrowserHistory } from 'history'
import { I18nextProvider } from 'react-i18next'

import {
  AuthContextProvider,
  ConfigContext,
  EnvironmentContextProvider,
  ErrorBoundary,
  OrgContextProvider,
  useAppConfig,
} from '@medable/medable-web-framework'
import { ThemeProvider } from '@medable/medable-web-ui'
import { DEFAULT_LOCALE, I18nService, useI18nTranslation } from '@medable/i18n-core'

import config from '@/app/config.json'
import { CUSTOM_I18N_CONFIG } from '@/i18n/i18n.constants'
import { Loading } from '@/components/loading'

const history = createBrowserHistory()

export const AppWrapper: FunctionComponent = (props) => {
  const { children } = props

  const { i18nInstance } = useI18nTranslation()
  const [appConfig, setAppConfig] = useState<unknown[]>([])
  const configContext = useAppConfig(appConfig)

  useEffect(() => {
    initializeI18n()
    getLocalConfig()
  }, [])

  const getLocalConfig = async () => {
    try {
      const response = await fetch('/app/config.local.json')
      const newAppConfig = response.ok ? [config, await response.json()] : [config]

      setAppConfig(newAppConfig)
    } catch (error) {
      setAppConfig([config])

      if (process.env.NODE_ENV !== 'production') {
        console.warn(error)
      }
    }
  }

  /**
   * After initializing our translations service we need to force the user to be on en-US
   * This is in order to prevent unnecessary inconsistencies whenever the user could be using a different locale
   */
  const initializeI18n = async () => {
    await I18nService.init(CUSTOM_I18N_CONFIG)
    await I18nService.setLanguage(DEFAULT_LOCALE)
  }

  if (!i18nInstance || Object.values(configContext[0]).length === 0) {
    return <Loading />
  }

  return (
    <ErrorBoundary>
      <Router history={history}>
        <I18nextProvider i18n={i18nInstance}>
          <ConfigContext.Provider value={configContext}>
            <EnvironmentContextProvider>
              <OrgContextProvider>
                <AuthContextProvider>
                  <ThemeProvider>{children}</ThemeProvider>
                </AuthContextProvider>
              </OrgContextProvider>
            </EnvironmentContextProvider>
          </ConfigContext.Provider>
        </I18nextProvider>
      </Router>
    </ErrorBoundary>
  )
}
